import { initContract } from '@ts-rest/core';
import { createApi, createQueryHooks } from './ts-rest';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';

/**
 * Design Studio Node Types
 */

export type DesignStudioNodeType = 'EMAIL' | 'FOLDER' | 'COMPONENT' | 'WHATSAPP';

export interface DesignStudioEnvelopeDetails {
  subject?: string;
  preheaderText?: string;
  fakeBcc?: boolean;
  recipient?: string;
  senderId?: number;
  replyToId?: number;
  customHeaders?: Array<{ name: string; value: string }>;
  [key: string]: unknown;
}

export interface DesignStudioNodeSettings {
  isTouched?: boolean;
  isTemplate?: boolean;
  canDisableGlobalStylesDefaults?: boolean;
  isGlobalStylesDefaultsDisabled?: boolean;
  visibility?: 'PRIVATE' | 'UNLISTED';
  /** Component name for COMPONENT nodes */
  name?: string;
  messageDetails?: {
    subject?: string;
  };
  personalization?: {
    data?: Record<string, string>;
    engine?: string | null;
    replacements?: Record<string, string>;
  };
  transformers?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DesignStudioNodeVariant {
  id: number;
  journeysParentEntityType: string;
  journeysAbTestParentEntityId: number;
  abTestState: string;
}

export interface DesignStudioNode {
  id: string;
  shortId: string;
  hydraWorkspaceId: number;
  type: DesignStudioNodeType;
  name: string;
  componentName: string | null;
  isTemplate: boolean;
  parentId: string | null;
  inboundId: string | null;
  settings: DesignStudioNodeSettings;
  envelopeDetails: DesignStudioEnvelopeDetails | null;
  updatedAt: string;
  createdAt: string;
  createdBy: number | null;
  updatedBy: number | null;
  updatedByName: string | null;
  language: string | null;
  languageGroupId: string | null;
  /** Present on EMAIL/WHATSAPP nodes; empty object `{}` on COMPONENT nodes */
  parts: {
    html?: string;
    text?: string;
    amp?: string;
    transformers?: null;
  };
  /** Present when linked to a journeys entity (campaign action, newsletter, transactional) */
  journeysTemplateId?: number;
  nodeId?: string;
  journeysParentEntityType?: string;
  journeysWorkspaceId?: number;
  /** Present as top-level fields when node is an A/B test variant */
  variantId?: number;
  variantParentEntityType?: string;
  journeysAbTestParentEntityId?: number;
  variantAbTestState?: string;
  /** Structured variant object (present on A/B test variant nodes) */
  variant?: DesignStudioNodeVariant;
}

export interface DesignStudioGetNodesPathParams {
  workspaceId: number;
}

const c = initContract();
export const contract = c.router({
  /**
   * Nodes API
   */
  getNodes: {
    method: 'GET',
    path: '/workspaces/:workspaceId/nodes',
    pathParams: c.type<DesignStudioGetNodesPathParams>(),
    responses: {
      200: c.type<DesignStudioNode[]>(),
    },
    summary: 'Get all nodes (emails, folders, components) for a workspace',
  },
});

export type Contract = typeof contract;

const { api } = createApi(contract, {
  baseUrl: '/design-studio/api',
  getHeaders: (): Record<string, string> => {
    const workspaceId = getWorkspaceIdFromCurrentUrl();
    return workspaceId != null ? { 'X-Workspace-Hydra-Id': String(workspaceId) } : {};
  },
});

const { useQuery, useMutation, useInfiniteQuery } = createQueryHooks(contract, api);

export {
  api as designStudioApi,
  useQuery as useDesignStudioQuery,
  useMutation as useDesignStudioMutation,
  useInfiniteQuery as useDesignStudioInfiniteQuery,
};
